import { LinkType, Reference } from '../CMSLink'
import { Slot } from '@radix-ui/react-slot'
import { cn } from '@telesoft/lib/web/cn'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

const buttonVariants = cva(
  'inline-flex items-center uppercase justify-center text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'text-black  bg-transparent border border-telesoft px-5 md:px-6 lg:px-8 py-3.5 md:py-3 lg:py-3.5 hover:text-black hover:border-telesoft hover:bg-telesoft hover:shadow-network',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'bg-transparent text-black border border-black px-5 md:px-6 lg:px-8 py-3.5 md:py-3 lg:py-3.5 hover:text-white hover:bg-heading hover:shadow-network',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost:
          'font-normal text-inherit bg-transparent border-none hover:border-none hover:bg-transparent block px-0 py-0',
        link: 'border-b border-gray-300 hover:border-telesoft',
      },
      size: {
        // TODO: fix these
        default: 'px-5 md:px-6 lg:px-8 py-3.5 md:py-3 lg:py-3.5',
        sm: 'px-3 md:px-4 lg:px-6 py-1.5 md:py-2 lg:py-2.5 text-xs',
        lg: 'h-11 px-8 text-lg',
        none: 'px-0 py-0',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  inverted?: boolean
  newTab?: boolean
  icon?: false | 'arrow' | 'search' | 'github' | 'plus'
  linkType?: LinkType
  reference?: Reference
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, newTab, inverted, ...props }, ref) => {
    const newTabProps = newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {}
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }), {
          'text-white': inverted,
        })}
        ref={ref}
        {...newTabProps}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
